import { Button, DatesSeparator } from '@koolumbus/web/ui';
import { createTxs, formatGuests, useLocale, useToggle } from '@koolumbus/web/utils';
import React from 'react';
import { formatDate } from '../../../../lib/helpers/date';
import { PublicBooking } from '../../../../lib/models';
import ChatDrawer from '../../../inbox/ChatDrawer';
import FloatingCardContent from './FloatingCardContent';

interface BookingContentProps {
  booking: PublicBooking;
}

const BookingContent: React.FC<React.PropsWithChildren<BookingContentProps>> = ({ booking }) => {
  const locale = useLocale();
  const tx = useTxs();

  const [isChatDrawerOpen, { off: closeChatDrawer, on: openChatDrawer }] = useToggle();

  return (
    <>
      <FloatingCardContent
        top={
          <div className="flex w-full justify-between items-center">
            <p className="font-semibold text-base md:text-lg text-gray-800">{tx.label}</p>

            <div className="flex items-center text-gray-500 font-medium">
              <span className="px-1">{formatDate(booking?.checkIn, locale)}</span>
              <div className="px-1">
                <DatesSeparator />
              </div>
              <span className="px-1">{formatDate(booking?.checkOut, locale)}</span>
            </div>
          </div>
        }
        center={
          <div className="flex flex-col w-full pl-2 space-y-1 text-gray-500">
            <span>
              {tx.guests}: {formatGuests(booking.adults ?? 1, booking.children ?? 0, locale)}
            </span>
            <span>
              {tx.deposit}: € {booking.deposit?.toLocaleString() ?? '-'}
            </span>
            <span>
              {tx.adminCosts}: € {booking.cleaningCost?.toLocaleString() ?? '-'}
            </span>
          </div>
        }
        bottom={
          <Button
            onClick={openChatDrawer}
            size="lg"
            className="w-full text-sm sm:text-base h-10 sm:font-medium"
          >
            {tx.openChat}
          </Button>
        }
      />

      {booking.chat && (
        <ChatDrawer chatId={booking.chat.id} isOpen={isChatDrawerOpen} onClose={closeChatDrawer} />
      )}
    </>
  );
};

const useTxs = createTxs({
  en: {
    label: 'My booking',
    openChat: 'OPEN CHAT',
    guests: 'Guests',
    deposit: 'Deposit',
    adminCosts: 'Administrative costs',
  },
  it: {
    label: 'La tua prenotazione',
    openChat: 'MOSTRA CHAT',
    guests: 'Ospiti',
    deposit: 'Deposito',
    adminCosts: 'Costi amministrativi',
  },
});

export default BookingContent;
